import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Globe, Lock, Edit, Trash2, Share2 } from "lucide-react";
import { Layout } from "@/components/Layout/Layout";
import { Button } from "@/components/ui/button";
import { useCollectionDetail, useCollectionPrompts, useDeleteCollection } from "@/hooks/collections";
import { useAuth } from "@/context/AuthContext";
import { ProfilePromptCard } from "@/components/User/ProfilePromptCard";
import { CreateCollectionDialog } from "@/components/Collections";
import { useState } from "react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";

const CollectionDetail = () => {
  const { collectionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data: collection, isLoading: loadingCollection } = useCollectionDetail(collectionId || "");
  const { data: prompts, isLoading: loadingPrompts } = useCollectionPrompts(collectionId || "");
  const deleteCollection = useDeleteCollection();

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const isOwner = user?.id === collection?.userId;

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/profile");
    }
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/collections/${collectionId}`;
    await navigator.clipboard.writeText(url);
    toast.success("Collection link copied!");
  };

  const handleDelete = () => {
    if (!collectionId) return;
    deleteCollection.mutate(collectionId, {
      onSuccess: () => {
        navigate("/profile");
      },
    });
  };

  if (loadingCollection) {
    return (
      <Layout>
        <div className="container max-w-4xl mx-auto px-4 py-8 space-y-6">
          <Skeleton className="h-10 w-32" />
          <Skeleton className="h-20 w-full" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-48 w-full" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  if (!collection) {
    return (
      <Layout>
        <div className="container max-w-4xl mx-auto px-4 py-8 text-center">
          <h1 className="text-2xl font-bold mb-4">Collection Not Found</h1>
          <p className="text-muted-foreground mb-4">
            This collection may have been deleted or you don't have access to view it.
          </p>
          <Button onClick={() => navigate("/profile")}>Go to Profile</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={handleBack}
          className="text-muted-foreground hover:text-foreground -ml-2"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <span className="text-4xl">{collection.emoji}</span>
            <div>
              <h1 className="text-2xl font-bold text-foreground">{collection.name}</h1>
              <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                {collection.isPublic ? (
                  <span className="flex items-center gap-1">
                    <Globe className="h-3 w-3" />
                    Public
                  </span>
                ) : (
                  <span className="flex items-center gap-1">
                    <Lock className="h-3 w-3" />
                    Private
                  </span>
                )}
                <span>•</span>
                <span>{prompts?.length || 0} prompts</span>
              </div>
            </div>
          </div>

          {isOwner && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleShare}>
                <Share2 className="h-4 w-4 mr-2" />
                Share
              </Button>
              <Button variant="outline" size="sm" onClick={() => setEditDialogOpen(true)}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDeleteDialogOpen(true)}
                className="text-destructive hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>

        {/* Description */}
        {collection.description && (
          <p className="text-muted-foreground">{collection.description}</p>
        )}

        {/* Prompts Grid */}
        {loadingPrompts ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-48 w-full" />
            ))}
          </div>
        ) : !prompts?.length ? (
          <div className="text-center py-12 bg-card/50 rounded-xl border border-border">
            <p className="text-muted-foreground">
              {isOwner
                ? "This collection is empty. Add prompts from any prompt card menu."
                : "This collection has no prompts yet."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {prompts.map((prompt) => (
              <ProfilePromptCard key={prompt.id} prompt={prompt} />
            ))}
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      {collection && (
        <CreateCollectionDialog
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          editingCollection={collection}
        />
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Collection?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{collection.name}". The prompts inside won't be deleted, just removed from this collection.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};

export default CollectionDetail;
